import { useEffect, useRef, useState } from 'react';
import { gameSounds } from '../../lib/gameSounds';

interface CrosswordClue {
  num: number;
  direction: 'across' | 'down';
  clue: string;
  answer: string;
  startRow: number;
  startCol: number;
}

interface CrosswordState {
  size: number;
  solved: number[];
  scores: Record<string, number>;
  endsAt: number;
  finished: boolean;
}

interface CrosswordConfig {
  clues: CrosswordClue[];
}

interface CellInfo {
  active: boolean;
  letter: string | null;
  num: number | null;
  revealed: boolean;
}

/**
 * Crossword overlay — redesigned to match the gameshow shell shared by Hype
 * Million / Chat Words / Hangman. Emerald palette so the four games read
 * as a series, each with a distinct color: gold / cyan / violet / emerald.
 */

const PALETTE = {
  bg: '#06140f',
  bgInner: '#0d2a1c',
  accent: '#10b981', // emerald-500
  accentLight: '#6ee7b7', // emerald-300
  accentDeep: '#047857', // emerald-700
  accentBorder: 'rgba(16,185,129,0.55)',
  accentBorderSoft: 'rgba(16,185,129,0.32)',
  accentGlow: 'rgba(16,185,129,0.45)',
  green: '#22c55e',
  red: '#ef4444',
  amber: '#fbbf24',
  text: '#fff',
  textDim: 'rgba(255,255,255,0.65)',
};

const ANIMATIONS = `
@keyframes cx-fadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes cx-spotlight { 0% { opacity: 0.3 } 50% { opacity: 0.55 } 100% { opacity: 0.3 } }
@keyframes cx-pulse { 0%, 100% { transform: scale(1) } 50% { transform: scale(1.04) } }
@keyframes cx-rise { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
@keyframes cx-cell-flip { 0% { transform: rotateY(90deg); opacity: 0 } 100% { transform: rotateY(0); opacity: 1 } }
@keyframes cx-solve-flash { 0% { background: rgba(34,197,94,0.18) } 60% { background: rgba(34,197,94,0.32) } 100% { background: rgba(34,197,94,0.12) } }
@keyframes cx-end-pop { 0% { transform: scale(0.85); opacity: 0 } 60% { transform: scale(1.04); opacity: 1 } 100% { transform: scale(1); opacity: 1 } }
`;

function buildGrid(size: number, clues: CrosswordClue[], solvedNums: Set<number>): CellInfo[][] {
  const grid: CellInfo[][] = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => ({
      active: false,
      letter: null,
      num: null,
      revealed: false,
    })),
  );
  for (const clue of clues) {
    const isSolved = solvedNums.has(clue.num);
    const startCell = grid[clue.startRow]?.[clue.startCol];
    if (startCell) {
      if (!startCell.num || clue.num < startCell.num) startCell.num = clue.num;
    }
    for (let i = 0; i < clue.answer.length; i++) {
      const r = clue.direction === 'down' ? clue.startRow + i : clue.startRow;
      const c = clue.direction === 'across' ? clue.startCol + i : clue.startCol;
      const cell = grid[r]?.[c];
      if (!cell) continue;
      cell.active = true;
      if (isSolved) {
        cell.revealed = true;
        cell.letter = clue.answer[i]?.toUpperCase() ?? null;
      }
    }
  }
  return grid;
}

function activeBounds(grid: CellInfo[][]) {
  let minR = grid.length;
  let maxR = -1;
  let minC = grid[0]?.length ?? 0;
  let maxC = -1;
  for (let r = 0; r < grid.length; r++) {
    for (let c = 0; c < (grid[r]?.length ?? 0); c++) {
      if (grid[r]![c]!.active) {
        if (r < minR) minR = r;
        if (r > maxR) maxR = r;
        if (c < minC) minC = c;
        if (c > maxC) maxC = c;
      }
    }
  }
  return { minR, maxR, minC, maxC };
}

export function CrosswordBody({
  state,
  config,
}: {
  state: CrosswordState;
  config: CrosswordConfig | null;
}) {
  const clues = config?.clues ?? [];
  const solvedSet = new Set(state.solved);
  const acrossClues = clues.filter((c) => c.direction === 'across').sort((a, b) => a.num - b.num);
  const downClues = clues.filter((c) => c.direction === 'down').sort((a, b) => a.num - b.num);
  const top = topScores(state.scores, 5);

  // Sound triggers
  const prevSolvedRef = useRef<number>(state.solved.length);
  const prevFinishedRef = useRef<boolean>(false);
  useEffect(() => {
    if (state.solved.length > prevSolvedRef.current) {
      void gameSounds.play('correct');
      void gameSounds.play('reveal');
    }
    prevSolvedRef.current = state.solved.length;
  }, [state.solved.length]);
  useEffect(() => {
    if (state.finished && !prevFinishedRef.current) {
      if (state.solved.length >= clues.length && clues.length > 0) {
        void gameSounds.play('winner');
        void gameSounds.play('celebration');
      } else {
        void gameSounds.play('loser');
      }
    }
    prevFinishedRef.current = state.finished;
  }, [state.finished, state.solved.length, clues.length]);

  if (state.finished) {
    return <Finished top={top} solved={state.solved.length} total={clues.length} />;
  }

  const grid = buildGrid(state.size, clues, solvedSet);
  const bounds = activeBounds(grid);
  const trimmedRows = bounds.minR <= bounds.maxR ? grid.slice(bounds.minR, bounds.maxR + 1) : grid;
  const trimmed = trimmedRows.map((row) =>
    bounds.minC <= bounds.maxC ? row.slice(bounds.minC, bounds.maxC + 1) : row,
  );

  return (
    <div style={stageStyle}>
      <style>{ANIMATIONS}</style>
      <Spotlight />

      <Header
        solved={state.solved.length}
        total={clues.length}
        endsAt={state.endsAt}
        leader={top[0] ?? null}
      />

      <main style={mainStyle}>
        <CommandStripe />
        <div style={bodyRowStyle}>
          <ClueList title="Across" clues={acrossClues} solvedSet={solvedSet} />
          <GridPanel grid={trimmed} />
          <ClueList title="Down" clues={downClues} solvedSet={solvedSet} />
        </div>
        <TopChattersStrip top={top} />
      </main>
    </div>
  );
}

/* ───────────────────── stage / shell ───────────────────── */

const stageStyle: React.CSSProperties = {
  position: 'relative',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 22,
  padding: '28px 36px',
  background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
  color: PALETTE.text,
  fontFamily:
    '"Rajdhani","Orbitron",ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  overflow: 'hidden',
  animation: 'cx-fadeIn 400ms ease both',
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  minHeight: 0,
};

const bodyRowStyle: React.CSSProperties = {
  flex: 1,
  display: 'grid',
  gridTemplateColumns: '320px 1fr 320px',
  gap: 22,
  minHeight: 0,
};

function Spotlight() {
  return (
    <>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(16,185,129,0.18) 0%, transparent 55%)',
          animation: 'cx-spotlight 6s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 0% 100%, rgba(4,120,87,0.12) 0%, transparent 55%)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}

/* ───────────────────── header ───────────────────── */

function Header({
  solved,
  total,
  endsAt,
  leader,
}: {
  solved: number;
  total: number;
  endsAt: number;
  leader: [string, number] | null;
}) {
  return (
    <header
      style={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 18,
        animation: 'cx-rise 500ms ease both',
      }}
    >
      <Brand />
      <ChipRow solved={solved} total={total} leader={leader} />
      <TimerRing endsAt={endsAt} />
    </header>
  );
}

function Brand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: 12,
          background: `linear-gradient(135deg, ${PALETTE.accent}, ${PALETTE.accentDeep})`,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: 3,
          padding: 12,
          boxShadow: `0 8px 28px ${PALETTE.accentGlow}`,
        }}
      >
        <div style={{ background: '#06140f', borderRadius: 2 }} />
        <div style={{ background: '#fff', borderRadius: 2 }} />
        <div style={{ background: '#fff', borderRadius: 2 }} />
        <div style={{ background: '#06140f', borderRadius: 2 }} />
      </div>
      <div>
        <div
          style={{
            fontSize: 12,
            letterSpacing: 4,
            color: PALETTE.accentLight,
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          The
        </div>
        <div
          style={{
            fontSize: 26,
            fontWeight: 900,
            letterSpacing: 3,
            lineHeight: 1,
            fontFamily: '"Orbitron","Rajdhani",sans-serif',
          }}
        >
          CROSSWORD
        </div>
      </div>
    </div>
  );
}

function ChipRow({
  solved,
  total,
  leader,
}: {
  solved: number;
  total: number;
  leader: [string, number] | null;
}) {
  return (
    <div
      style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}
    >
      <Chip label="Solved" value={`${solved} / ${total}`} highlight />
      {leader && <Chip label="Leader" value={`${leader[0]} · ${leader[1]}`} />}
    </div>
  );
}

function Chip({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        padding: '10px 18px',
        background: 'rgba(255,255,255,0.05)',
        border: `1px solid ${highlight ? PALETTE.accentBorder : PALETTE.accentBorderSoft}`,
        borderRadius: 999,
        backdropFilter: 'blur(8px)',
        boxShadow: highlight ? `0 0 24px -8px ${PALETTE.accentGlow}` : undefined,
      }}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 2,
          color: PALETTE.textDim,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: highlight ? PALETTE.accentLight : PALETTE.text,
          fontFamily: '"JetBrains Mono","Orbitron",monospace',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function TimerRing({ endsAt }: { endsAt: number }) {
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)),
  );
  useEffect(() => {
    const tick = () => setSecondsLeft(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [endsAt]);
  const totalSeconds = Math.max(secondsLeft, 60);
  const pct = Math.max(0, Math.min(1, secondsLeft / totalSeconds));
  const urgent = secondsLeft <= 30;
  const r = 32;
  const c = 2 * Math.PI * r;
  const m = Math.floor(secondsLeft / 60);
  const s = String(secondsLeft % 60).padStart(2, '0');
  return (
    <div
      style={{
        position: 'relative',
        width: 92,
        height: 92,
        display: 'grid',
        placeItems: 'center',
        animation: urgent ? 'cx-pulse 600ms ease-in-out infinite' : undefined,
      }}
    >
      <svg
        width={92}
        height={92}
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
      >
        <circle cx={46} cy={46} r={r} stroke="rgba(255,255,255,0.10)" strokeWidth={5} fill="none" />
        <circle
          cx={46}
          cy={46}
          r={r}
          stroke={urgent ? PALETTE.red : PALETTE.accent}
          strokeWidth={5}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 500ms linear' }}
        />
      </svg>
      <div
        style={{
          fontFamily: '"JetBrains Mono",monospace',
          fontSize: 18,
          fontWeight: 900,
          color: urgent ? PALETTE.red : PALETTE.accentLight,
        }}
      >
        {m}:{s}
      </div>
    </div>
  );
}

/* ───────────────────── command stripe ───────────────────── */

function CommandStripe() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '12px 18px',
        background:
          'linear-gradient(90deg, rgba(16,185,129,0.10), rgba(16,185,129,0.18), rgba(16,185,129,0.10))',
        border: `1px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 14,
        fontSize: 16,
        fontWeight: 600,
        color: PALETTE.textDim,
      }}
    >
      <span style={{ color: PALETTE.text }}>Just</span>
      <span
        style={{
          fontFamily: '"JetBrains Mono",monospace',
          padding: '4px 10px',
          background: 'rgba(34,197,94,0.18)',
          border: `1px solid ${PALETTE.green}`,
          borderRadius: 8,
          color: '#6ee7b7',
          fontWeight: 800,
          letterSpacing: 1,
        }}
      >
        say the answer
      </span>
      <span>in chat to lock it in</span>
    </div>
  );
}

/* ───────────────────── clue list ───────────────────── */

function ClueList({
  title,
  clues,
  solvedSet,
}: {
  title: string;
  clues: CrosswordClue[];
  solvedSet: Set<number>;
}) {
  return (
    <div
      style={{
        position: 'relative',
        zIndex: 1,
        padding: 18,
        background: 'linear-gradient(180deg, rgba(13,42,28,0.85), rgba(6,20,15,0.95))',
        border: `2px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 18,
        boxShadow: '0 18px 48px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
        animation: 'cx-rise 500ms ease both',
      }}
    >
      <div
        style={{
          fontFamily: '"Orbitron",sans-serif',
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: 4,
          textTransform: 'uppercase',
          color: PALETTE.accentLight,
          textShadow: `0 0 12px ${PALETTE.accentGlow}`,
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {clues.map((c) => {
          const solved = solvedSet.has(c.num);
          return (
            <div
              key={c.num}
              style={{
                display: 'grid',
                gridTemplateColumns: '32px 1fr auto',
                alignItems: 'baseline',
                gap: 10,
                padding: '8px 12px',
                borderRadius: 8,
                background: solved ? 'rgba(34,197,94,0.14)' : 'rgba(255,255,255,0.02)',
                border: solved ? '1px solid rgba(34,197,94,0.35)' : '1px solid transparent',
                transition: 'background 0.3s, border-color 0.3s',
                animation: solved ? 'cx-solve-flash 1s ease-out' : undefined,
              }}
            >
              <span
                style={{
                  fontFamily: '"JetBrains Mono",monospace',
                  fontSize: 14,
                  fontWeight: 900,
                  color: solved ? '#4ade80' : PALETTE.accentLight,
                }}
              >
                {c.num}
              </span>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: solved ? PALETTE.text : 'rgba(255,255,255,0.82)',
                  textDecoration: solved ? 'line-through' : undefined,
                  textDecorationColor: solved ? 'rgba(34,197,94,0.6)' : undefined,
                }}
              >
                {c.clue}
              </span>
              <span
                style={{
                  fontFamily: '"JetBrains Mono",monospace',
                  fontSize: 12,
                  fontWeight: 800,
                  color: solved ? '#4ade80' : 'rgba(255,255,255,0.4)',
                }}
              >
                {solved ? c.answer.toUpperCase() : `(${c.answer.length})`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────── grid ───────────────────── */

function GridPanel({ grid }: { grid: CellInfo[][] }) {
  const cols = grid[0]?.length ?? 0;
  return (
    <div
      style={{
        position: 'relative',
        zIndex: 1,
        padding: 24,
        background: 'linear-gradient(180deg, rgba(13,42,28,0.85), rgba(6,20,15,0.95))',
        border: `2px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 18,
        boxShadow: '0 18px 48px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
        display: 'grid',
        placeItems: 'center',
        animation: 'cx-rise 500ms ease both',
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(34px, 1fr))`,
          gap: 4,
          maxWidth: '100%',
          maxHeight: '100%',
        }}
      >
        {grid.flatMap((row, r) => row.map((cell, c) => <Cell key={`${r}-${c}`} cell={cell} />))}
      </div>
    </div>
  );
}

function Cell({ cell }: { cell: CellInfo }) {
  if (!cell.active) {
    return (
      <div
        style={{
          aspectRatio: '1 / 1',
          background: 'rgba(0,0,0,0.45)',
          borderRadius: 4,
        }}
      />
    );
  }
  const revealed = cell.revealed && cell.letter;
  return (
    <div
      style={{
        position: 'relative',
        aspectRatio: '1 / 1',
        background: revealed
          ? `linear-gradient(180deg, ${PALETTE.accent}, ${PALETTE.accentDeep})`
          : 'rgba(255,255,255,0.04)',
        border: revealed ? `2px solid ${PALETTE.accentLight}` : `1px solid rgba(255,255,255,0.15)`,
        borderRadius: 6,
        display: 'grid',
        placeItems: 'center',
        boxShadow: revealed
          ? `0 4px 14px ${PALETTE.accentGlow}, inset 0 1px 0 rgba(255,255,255,0.18)`
          : undefined,
        animation: revealed ? 'cx-cell-flip 0.5s cubic-bezier(0.16,1,0.3,1) both' : undefined,
        transformStyle: 'preserve-3d',
      }}
    >
      {cell.num != null && (
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: 4,
            fontFamily: '"JetBrains Mono",monospace',
            fontSize: 9,
            fontWeight: 900,
            color: revealed ? 'rgba(255,255,255,0.85)' : PALETTE.accentLight,
            lineHeight: 1,
          }}
        >
          {cell.num}
        </span>
      )}
      {revealed && (
        <span
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 22,
            fontWeight: 900,
            color: '#fff',
            textShadow: '0 2px 4px rgba(0,0,0,0.55)',
          }}
        >
          {cell.letter}
        </span>
      )}
    </div>
  );
}

/* ───────────────────── footer strip — top chatters ───────────────────── */

function TopChattersStrip({ top }: { top: Array<[string, number]> }) {
  if (top.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 14px',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 10,
          fontSize: 12,
          color: 'rgba(255,255,255,0.4)',
          fontStyle: 'italic',
          justifyContent: 'center',
        }}
      >
        No one has scored yet — be the first to say an answer in chat
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '8px 14px',
        background: 'rgba(255,255,255,0.03)',
        border: `1px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          fontFamily: '"Orbitron",sans-serif',
          fontSize: 10,
          letterSpacing: 3,
          color: PALETTE.accentLight,
          fontWeight: 800,
        }}
      >
        TOP CHATTERS
      </span>
      <div style={{ display: 'flex', gap: 10, flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {top.map(([name, score], i) => (
          <span
            key={name}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              background:
                i === 0
                  ? 'linear-gradient(90deg, rgba(16,185,129,0.30), rgba(16,185,129,0.10))'
                  : 'rgba(255,255,255,0.04)',
              border: i === 0 ? `1px solid ${PALETTE.accent}` : '1px solid transparent',
              fontSize: 13,
            }}
          >
            <span
              style={{
                fontFamily: '"JetBrains Mono",monospace',
                fontWeight: 900,
                color: PALETTE.accentLight,
              }}
            >
              #{i + 1}
            </span>
            <span style={{ color: PALETTE.text, fontWeight: 600 }}>{name}</span>
            <span
              style={{
                fontFamily: '"JetBrains Mono",monospace',
                color: PALETTE.accentLight,
                fontWeight: 800,
              }}
            >
              {score}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────── finished ───────────────────── */

function Finished({
  top,
  solved,
  total,
}: {
  top: Array<[string, number]>;
  solved: number;
  total: number;
}) {
  const allDone = total > 0 && solved >= total;
  return (
    <div
      style={{
        flex: 1,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 22,
        padding: 40,
        background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
        color: PALETTE.text,
        fontFamily: '"Rajdhani","Orbitron",sans-serif',
      }}
    >
      <style>{ANIMATIONS}</style>
      <div
        style={{
          padding: '48px 60px',
          borderRadius: 24,
          background: 'rgba(0,0,0,0.4)',
          border: `2px solid ${allDone ? PALETTE.green : PALETTE.accentBorder}`,
          boxShadow: `0 0 100px -24px ${allDone ? 'rgba(34,197,94,0.6)' : PALETTE.accentGlow}`,
          textAlign: 'center',
          minWidth: 520,
          animation: 'cx-end-pop 600ms cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        <div style={{ fontSize: 64, marginBottom: 12 }}>{allDone ? '🏆' : '⏳'}</div>
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 14,
            letterSpacing: 4,
            color: PALETTE.accentLight,
            textTransform: 'uppercase',
            fontWeight: 800,
          }}
        >
          Crossword complete
        </div>
        <div
          style={{
            marginTop: 12,
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 48,
            fontWeight: 900,
            color: PALETTE.text,
            textShadow: `0 0 28px ${PALETTE.accentGlow}`,
          }}
        >
          <span style={{ color: PALETTE.accentLight }}>{solved}</span>
          <span style={{ color: PALETTE.textDim, fontSize: 32 }}> / {total}</span>
        </div>
        <div style={{ fontSize: 12, letterSpacing: 2, color: PALETTE.textDim, marginTop: 4 }}>
          CLUES SOLVED
        </div>
        {top.length > 0 && (
          <div
            style={{
              marginTop: 28,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              minWidth: 360,
            }}
          >
            {top.map(([name, score], i) => (
              <div
                key={name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '10px 18px',
                  borderRadius: 10,
                  background:
                    i === 0
                      ? `linear-gradient(90deg, ${PALETTE.accentBorder}, rgba(255,255,255,0.04))`
                      : 'rgba(255,255,255,0.04)',
                  border: i === 0 ? `1px solid ${PALETTE.accent}` : '1px solid transparent',
                  boxShadow: i === 0 ? `0 0 26px -10px ${PALETTE.accentGlow}` : undefined,
                  fontSize: 16,
                }}
              >
                <span
                  style={{
                    fontFamily: '"JetBrains Mono",monospace',
                    fontWeight: 900,
                    color: PALETTE.accentLight,
                    minWidth: 32,
                  }}
                >
                  #{i + 1}
                </span>
                <span style={{ flex: 1, color: PALETTE.text }}>{name}</span>
                <span
                  style={{
                    fontFamily: '"JetBrains Mono",monospace',
                    fontWeight: 900,
                    color: PALETTE.text,
                  }}
                >
                  {score}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────── helpers ───────────────────── */

function topScores(scores: Record<string, number>, n: number): Array<[string, number]> {
  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);
}
